import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, LayoutGrid, LogOut, User, Settings, Bell, BellDot, X, CheckCircle, MessageSquare, Loader, AlertTriangle, Clock, PenTool } from 'lucide-react'
import { getAccessibleNavModules, isModuleReleased } from '../constants/modules'
import { bidWorkupService, identityService, timekeepingService } from '../api'
import { routeForNotification } from '../notifications/notificationLink'
import { parseUTC } from '../../utils/dateUtils'
import { useTenantBranding } from '../../contexts/TenantContext'
import { useModuleSwitches } from '../../contexts/ModuleSwitchContext.jsx'
import TourLauncher from '../tour/TourLauncher.jsx'
import WhatChangedButton from './WhatChangedButton.jsx'
import { useT } from '../i18n/LanguageContext.jsx'
import { signOutStorage } from '../auth/signOut'
import { Z } from '../styles/zLayers'
import usePublishedHeight from '../hooks/usePublishedHeight'

/**
 * Unified header component for all pages in the application
 *
 * THE BEST VALUE PER STRING IN THE APPLICATION. These twenty-odd words sit on top of every
 * authenticated screen, including the hundreds this branch has NOT translated — so translating them is
 * what makes a partly-covered application read as "Spanish, with some English left in it" instead of
 * "English, with a Spanish login page".
 *
 * MODULE NAMES COME FROM `t(m.id) || m.label`, never a bare `t(m.id)`. A module added to NAV_MODULES
 * without an entry in the shared i18n modules dictionary is absent from the BASE dictionary too, and `makeT` answers ''
 * for that — a blank nav button, which is the one output this whole design forbids. The registry's own
 * English label is the fallback, so a forgotten dictionary entry degrades to English like everything
 * else does.
 */
const AppHeader = ({ 
  title, 
  showPortalButton = true, 
  showProfileButton = true,
  showAdminButton = true,
  user 
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { features } = useTenantBranding()
  const { switches } = useModuleSwitches()
  const t = useT('chrome')
  const tm = useT('modules')

  // The measured height of this whole sticky block, published to `:root` as `--app-header-h`.
  // Everything that stacks below it derives its offset from that instead of hardcoding 92px.
  const headerRef = usePublishedHeight('--app-header-h')

  // Pass the tenant's feature flags so feature-gated modules (e.g. Forms) only show when enabled, and
  // the server's module switches so a held module (Capital, Capex, Invoicing) has no tab until released.
  const navModules = getAccessibleNavModules(user, features, switches)

  const isNavActive = (path) => {
    if (path === '/portal') return location.pathname === '/portal'
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const userName = user?.first_name && user?.last_name 
    ? `${user.first_name} ${user.last_name}` 
    : user?.email || 'User'

  const showBackButton = !['/portal', '/login', '/'].includes(location.pathname)
  const isAdmin = user?.role === 'master_admin' || user?.role === 'admin'

  // THE ONLY LOGOUT BUTTON IN THE APPLICATION (components/Navigation.jsx has no importers), so what
  // it forgets is what the app forgets. It used to call localStorage.clear() inline; the reasons that
  // is now one shared function — and the three keys it deliberately keeps — are in shared/auth/signOut.js.
  const handleLogout = async () => {
    try {
      await identityService.logout()
    } catch (_) {
      // continue even if backend revocation fails
    }
    signOutStorage()
    navigate('/login')
  }

  return (
    // Everything that sticks below this bar reads `--app-header-h`. Measured, because the
    // height moves with the logo, the rem padding and the `flex-wrap` at 768px.
    <div ref={headerRef} className="ah-sticky-outer">
      <style>{`
        .ah-sticky-outer {
          position: sticky;
          top: 0;
          /* The "header" rung of shared/styles/zLayers.js. Above the page, below every overlay a
             user has to reach on top of the page — which is the whole reason that file exists.
             Kept a literal because two tests read it back out of this source with a regex;
             zLayers.contract.test.jsx fails if this number and the rung disagree. */
          z-index: 200;
          box-shadow: 0 2px 8px rgba(0,0,0,0.25);
        }
        .ah-root {
          background: linear-gradient(135deg, var(--secondary-color) 0%, var(--primary-color) 100%);
          color: white;
          padding: 0.75rem 1.5rem;
        }
        .ah-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }
        .ah-left {
          display: flex;
          align-items: center;
          gap: 1rem;
          min-width: 0;
          flex: 1;
        }
        .ah-logo { height: 38px; width: auto; flex-shrink: 0; }
        .ah-title {
          font-size: 1.4rem;
          font-weight: 700;
          margin: 0;
          font-family: 'Roboto Slab', serif;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ah-right {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-shrink: 0;
        }
        .ah-userinfo { text-align: right; line-height: 1.3; }
        .ah-userinfo-name { font-size: 0.875rem; font-weight: 600; margin: 0; white-space: nowrap; }
        .ah-userinfo-role { font-size: 0.7rem; opacity: 0.8; margin: 0; white-space: nowrap; }
        @media (max-width: 1100px) { .ah-userinfo-role { display: none; } }
        @media (max-width: 920px)  { .ah-userinfo { display: none; } }
        /* THE VERSION LIVES ON THE TITLE ROW, NOT IN THE USER BLOCK, AND THAT IS THE WHOLE POINT.
           It used to sit inside .ah-userinfo-role, which is display:none under 1100px — and the
           whole .ah-userinfo goes under 920px and again under 768px. So on every phone the version
           was gone, and the owner could not tell whether what he was testing was what had just
           shipped: "we need a way to check version number after login. It is not visible on a
           mobile device in the current setup."
           Deliberately NO media query hides this, and AppHeader.versionIsVisibleOnAPhone.test.jsx
           fails if one ever does. Opacity is 0.55 rather than the old 0.3, which was close enough
           to invisible that it did not answer the question on a desktop either. */
        .ah-version {
          font-size: 0.68rem;
          font-weight: 600;
          opacity: 0.55;
          letter-spacing: 0.02em;
          white-space: nowrap;
          flex-shrink: 0;
          align-self: center;
        }
        .ah-btn {
          background: rgba(255,255,255,0.15);
          border: 1px solid rgba(255,255,255,0.3);
          color: white;
          padding: 0.45rem 0.9rem;
          border-radius: 0.5rem;
          cursor: pointer;
          font-size: 0.85rem;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.4rem;
          white-space: nowrap;
          transition: background 0.15s ease;
        }
        .ah-btn:hover { background: rgba(255,255,255,0.28); }
        .ah-btn-logout { background: #dc2626; border: none; }
        .ah-btn-logout:hover { background: #b91c1c; }
        /* EVERYTHING IN THIS HEADER IS TOO SMALL TO HIT WITH A THUMB, AND IT IS ON EVERY SCREEN.
           Measured at 390x844 across 132 rendered screens on 2026-08-11: ".ah-btn" came out
           87.7x31.4 and 36.2x31.4, ".ah-bell-btn" 35.2x35.2, ".ah-nav-item" 42.1x36. The 44px
           floor is the one both Apple's and Google's guidance land on, and every one of those
           fails it on BOTH axes — so the app header alone accounted for 2,598 of the audit's
           4,175 under-size tap targets, more than every page in the app put together.

           Deliberately scoped to <=768px and nothing wider. This app is used on office monitors all
           day and a desktop header that suddenly grew 40% taller would be a regression paid for by
           the people who use it most; at 1440 and 1920 not one declaration below applies. */
        @media (max-width: 768px) {
          .ah-root { padding: 0.6rem 1rem; }
          .ah-inner { flex-wrap: wrap; gap: 0.5rem; }
          /* THE TITLE ROW IS A COMPETITION AND THE TITLE WAS LOSING IT BADLY.
             ".ah-left" is the whole first row on a phone — logo, Back, Portal, the page title, the
             version — and ".ah-title" is the only item in it that can shrink, so it absorbs every
             pixel the others take. Measured at 390x844 before this block: the title box was 43.4px
             wide, and at 360x740 it was 13.4px. Not "Human Resources Management" truncated — "Fo…"
             where the word was "Forms". 130 of the 148 measured screens were clipping their own
             title, 12,430px of text in total, the worst single repeated finding in the audit.

             16 of those pixels per screen were a regression from the 44px tap floor above: Back and
             Portal are ".ah-btn", so min-width 44px grew each of them from ~36px and took the
             difference off the title on every screen showing both. A 0.5rem gap gives back 32px —
             twice what the tap floor cost — without shrinking a single touch target.

             The logo cap is the other half. "height: 30px; width: auto" lets a wide tenant logo take
             whatever its aspect ratio asks for, and branding is not worth more than knowing which
             screen you are on. 84px is a hard ceiling, with object-fit contain so it letterboxes
             rather than distorting; a logo already narrower than that is untouched. */
          .ah-left { width: 100%; flex-shrink: 0; gap: 0.5rem; }
          .ah-title { font-size: 1rem; }
          .ah-logo { height: 30px; max-width: 84px; object-fit: contain; }
          .ah-right { width: 100%; justify-content: flex-end; flex-wrap: wrap; gap: 0.4rem; }
          .ah-btn-label { display: none; }
          .ah-btn { padding: 0.45rem 0.6rem; min-height: 44px; min-width: 44px; justify-content: center; }
          .ah-userinfo { display: none; }
        }
        @media (max-width: 400px) {
          .ah-right { justify-content: space-between; }
          .ah-btn { flex: 1; justify-content: center; }
        }
        .ah-nav-strip {
          background: #111;
          border-bottom: 1px solid rgba(255,255,255,0.07);
          padding: 0 1rem;
          display: flex;
          align-items: stretch;
          overflow-x: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .ah-nav-strip::-webkit-scrollbar { display: none; }
        .ah-nav-home {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.5rem 1rem 0.5rem 0.5rem;
          color: rgba(255,255,255,0.55);
          font-size: 0.75rem;
          font-weight: 500;
          background: none;
          border: none;
          border-right: 1px solid rgba(255,255,255,0.1);
          border-bottom: 2px solid transparent;
          cursor: pointer;
          white-space: nowrap;
          margin-right: 0.25rem;
          transition: color 0.15s, background 0.15s;
          flex-shrink: 0;
        }
        .ah-nav-home:hover { color: rgba(255,255,255,0.9); background: rgba(255,255,255,0.06); }
        .ah-nav-home.ah-nav-active { color: white; border-bottom-color: #dc2626; font-weight: 600; }
        .ah-nav-item {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.5rem 0.8rem;
          color: rgba(255,255,255,0.55);
          font-size: 0.75rem;
          font-weight: 500;
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          cursor: pointer;
          white-space: nowrap;
          transition: color 0.15s, border-color 0.15s, background 0.15s;
          flex-shrink: 0;
        }
        .ah-nav-item:hover { color: rgba(255,255,255,0.9); background: rgba(255,255,255,0.06); }
        .ah-nav-item.ah-nav-active { color: white; border-bottom-color: #dc2626; font-weight: 600; }
        .ah-nav-lbl { display: inline; }
        /* Below 520px the labels go and each module becomes its emoji alone — which measured
           42.1x36, i.e. under the 44px floor on both axes, fourteen times over on every screen in
           the app (1,734 of the audit's tap-target findings). The strip already scrolls sideways
           ("overflow-x: auto" above), so widening the buttons costs nothing but the swipe that was
           always there. Above 520px the labels are back and the buttons are wide anyway, so this
           rule is scoped to exactly the case that fails. */
        @media (max-width: 520px) {
          .ah-nav-lbl { display: none; }
          .ah-nav-item, .ah-nav-home { min-width: 44px; min-height: 44px; justify-content: center; }
        }
        /* Notification Bell */
        .ah-bell-btn {
          background: rgba(255,255,255,0.10);
          border: 1px solid rgba(255,255,255,0.2);
          color: white;
          width: 2.2rem;
          height: 2.2rem;
          border-radius: 0.5rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          flex-shrink: 0;
          transition: background 0.15s ease;
        }
        .ah-bell-btn:hover { background: rgba(255,255,255,0.25); }
        /* THE BELL, AT 44px, AND IT HAS TO BE DECLARED DOWN HERE.
           The first attempt put this in the max-width:768px block up with the other touch sizes —
           and it did nothing, because the .ah-bell-btn width: 2.2rem above is declared LATER in
           the same stylesheet at the same specificity, so the cascade threw the media rule away.
           A source-order override is invisible to a source test that only asks "does a 44px rule
           exist inside a media query" — it did, and the bell stayed 35.2px on all 124 screens
           until the browser measured it again. Hence this rule sits after the one it overrides,
           and zLayers-style contract testing was extended to check the order too. */
        @media (max-width: 768px) {
          .ah-bell-btn { width: 44px; height: 44px; }
        }
        .ah-bell-badge {
          position: absolute;
          top: -0.2rem;
          right: -0.2rem;
          min-width: 1.1rem;
          height: 1.1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 9999px;
          background: #dc2626;
          color: white;
          font-size: 10px;
          font-weight: 700;
          padding: 0 3px;
          pointer-events: none;
        }
      `}</style>

      <header className="ah-root">
        <div className="ah-inner">
          <div className="ah-left">
            <img src={identityService.getAppLogoUrl('dark')} alt={t('companyLogo')} className="ah-logo" />
            {showBackButton && (
              <button className="ah-btn" onClick={() => navigate(-1)} title={t('goBack')}>
                <ArrowLeft size={15} />
                <span className="ah-btn-label">{t('back')}</span>
              </button>
            )}
            {showPortalButton && (
              // THE ICON IS NOT DECORATION HERE, IT IS THE ONLY THING LEFT.
              // `@media (max-width: 768px)` hides `.ah-btn-label`, and this was the one .ah-btn
              // with no icon behind it — so on every phone screen in the app it rendered as a
              // blank pill you could press but not read. 82 of the audit's 128 blank controls
              // were this single button. A `title` does not rescue it: a phone has no hover.
              <button className="ah-btn" onClick={() => navigate('/portal')} title={t('portal')}>
                <LayoutGrid size={15} />
                <span className="ah-btn-label">{t('portal')}</span>
              </button>
            )}
            <h1 className="ah-title">{title}</h1>
            <span className="ah-version" title={t('appVersion')} data-testid="app-version">
              v{import.meta.env.VITE_APP_VERSION || '2.184.3'}
            </span>
          </div>

          <div className="ah-right">
            <div className="ah-userinfo">
              <p className="ah-userinfo-name">{userName}</p>
              {/* The version moved to the title row (.ah-version) so a phone can see it. Not
                  repeated here, or a desktop reads it twice. */}
              <p className="ah-userinfo-role">{user?.role || t('employee')}</p>
            </div>
            {user && <NotificationBellHeader />}
            {/* Guided tours. Here rather than floating in a corner: every bottom corner of this app
                is already occupied by some screen's own action bar or FAB. Renders nothing on its
                own when the user has no eligible tour, and nothing at all outside a TourProvider. */}
            <TourLauncher />
            {/* The post-update notice, on demand. Was a modal that opened itself at every sign-in. */}
            {user && <WhatChangedButton />}
            {showProfileButton && (
              <button className="ah-btn" onClick={() => navigate('/profile')} title={t('profile')}>
                <User size={15} />
                <span className="ah-btn-label">{t('profile')}</span>
              </button>
            )}
            {showAdminButton && isAdmin && (
              <button className="ah-btn" onClick={() => navigate('/admin')} title={t('admin')}>
                <Settings size={15} />
                <span className="ah-btn-label">{t('admin')}</span>
              </button>
            )}
            <button className="ah-btn ah-btn-logout" onClick={handleLogout} title={t('logout')}>
              <LogOut size={15} />
              <span className="ah-btn-label">{t('logout')}</span>
            </button>
          </div>
        </div>
      </header>

      {user && navModules.length > 0 && (
        <nav className="ah-nav-strip">
          <button
            className={`ah-nav-home${isNavActive('/portal') ? ' ah-nav-active' : ''}`}
            onClick={() => navigate('/portal')}
            title={t('dashboard')}
          >
            🏠 <span className="ah-nav-lbl">{t('dashboard')}</span>
          </button>
          {navModules.map(m => (
            <button
              key={m.id}
              className={`ah-nav-item${isNavActive(m.path) ? ' ah-nav-active' : ''}`}
              onClick={() => navigate(m.path)}
              title={tm(m.navKey || m.id) || m.label}
            >
              {m.icon} <span className="ah-nav-lbl">{tm(m.navKey || m.id) || m.label}</span>
            </button>
          ))}
        </nav>
      )}
    </div>
  )
}

// ── Global Notification Bell ─────────────────────────────────────────────────

/** What the panel asks for. It gets less when the screen has less — see `place()` below. */
const PANEL_W = 360

function NotificationBellHeader() {
  const navigate = useNavigate()
  const t = useT('chrome')
  // The pending-daily-reports count is Timekeeping's. With Timekeeping switched off for this company
  // (Super Admin's module switches) its service refuses the read, so the bell does not ask - read
  // through a ref so the minute poll below always sees the current answer. The notifications
  // themselves are everybody's and keep coming whatever is switched off.
  const { switches } = useModuleSwitches()
  const timekeepingOn = useRef(true)
  timekeepingOn.current = isModuleReleased('timekeeping', switches)
  const [unread, setUnread] = useState(0)
  const [notifications, setNotifications] = useState([])
  const [pendingReports, setPendingReports] = useState([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const dropdownRef = useRef(null)
  // WHERE THE PANEL GOES, MEASURED RATHER THAN ASSUMED.
  //
  // It used to be `position: absolute; right: 0; width: 360px` inside the bell's wrapper. On a
  // desktop that reads as "hang it off the right of the bell" and looks fine. On a 390px phone the
  // bell is nowhere near the right edge — Profile, Admin and Logout sit to its right — so the
  // panel's LEFT edge landed at bellRight - 360, i.e. off the left of the screen, and the owner
  // photographed it that way this morning with its content sliced off outside the viewport.
  //
  // Right-aligning to the trigger is only correct while there is room for it. So: one code path,
  // `position: fixed`, coordinates measured off the bell and then CLAMPED to the viewport. A
  // desktop still gets a 360px panel hanging under the bell (nothing moves); a phone gets one that
  // spans the screen with an 8px margin. Fixed also escapes any clipping ancestor, which is the
  // same reason AnchoredMenu portals — see its header comment.
  const [box, setBox] = useState(null)

  const APPROVAL_ROLES = ['master_admin', 'admin', 'manager', 'superintendent']

  // Poll pending daily reports and connect to global notifications SSE
  useEffect(() => {
    let active = true

    // 1. Pending Reports Polling
    const pollReports = async () => {
      if (!timekeepingOn.current) {
        if (active) setPendingReports([])
        return
      }
      try {
        const user = JSON.parse(localStorage.getItem('bfs_user') || '{}')
        const role = (user.role || '').toLowerCase()
        const isForemanRole = ['foreman', 'field_worker', 'employee'].includes(role)
        const params = { limit: 200 }
        if (isForemanRole) {
          const empId = user.employee_id ?? user.id
          if (empId) params.foreman_id = empId
        }
        const d = await timekeepingService.getDailyReports(params)
        const items = d?.items ?? []
        const pending = items.filter(r => ['submitted', 'timekeeper_reviewed'].includes(r.status))
        const relevant = APPROVAL_ROLES.includes(role)
          ? pending
          : pending.filter(r => r.foreman_id === user.id || r.foreman_id === user.employee_id)
        if (active) setPendingReports(relevant)
      } catch { /* silently fail */ }
    }
    pollReports()
    const id = setInterval(pollReports, 60_000)

    // 2. Global Notifications SSE
    let eventSource = null
    // B2: put a short-lived TICKET in the EventSource URL instead of the full access token (a URL ends
    // up in nginx/ALB logs, history and Referer). A FRESH ticket is minted on every (re)connect, so the
    // ~45s expiry never strands the bell — the 5s reconnect backoff is well inside that window.
    const connectSSE = async () => {
      // Phase B3: never gate on the in-memory access token — after a reload there is none even
      // though the session is valid, which would leave the notification bell permanently dead.
      if (!localStorage.getItem('bfs_user')) return

      // Use bidworkup base URL but replace /api/v1 with actual path
      const baseUrl = bidWorkupService.getNotificationsUrl?.() || '/api/v1/notifications/stream'
      let url
      try {
        const { ticket } = await bidWorkupService.getStreamTicket()
        url = `${baseUrl}?ticket=${encodeURIComponent(ticket)}`
      } catch {
        // Phase B3: the ?token= fallback is gone — no readable access token exists to put in a URL.
        // Fail closed and let the existing reconnect backoff mint a fresh ticket.
        return
      }
      if (!active) return // unmounted while awaiting the ticket

      eventSource = new EventSource(url)

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          if (data.type === 'count_update') {
            setUnread(data.unread_count)
          } else if (data.type === 'notification') {
            setNotifications(prev => {
              // Avoid duplicates
              if (prev.some(n => n.id === data.notification.id)) return prev
              return [data.notification, ...prev].slice(0, 50)
            })
          }
        } catch (err) {
          console.error("SSE Parse Error", err)
        }
      }

      eventSource.onerror = () => {
        eventSource.close()
        // Reconnect after 5 seconds
        setTimeout(() => { if (active) connectSSE() }, 5000)
      }
    }
    
    connectSSE()

    return () => { 
      active = false
      clearInterval(id)
      if (eventSource) eventSource.close()
    }
  }, [])

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Measure while open, and again if the viewport changes under it (rotation, the on-screen
  // keyboard, a desktop window drag). The header is `position: sticky; top: 0`, so the bell does
  // not move on a vertical scroll and there is no scroll listener to pay for.
  useLayoutEffect(() => {
    if (!open) return undefined
    const place = () => {
      const el = dropdownRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      const vw = document.documentElement.clientWidth || window.innerWidth || 0
      const vh = document.documentElement.clientHeight || window.innerHeight || 0
      const EDGE = 8
      // As wide as it wants, never wider than the screen allows.
      const width = Math.min(PANEL_W, Math.max(0, vw - EDGE * 2))
      // Right-aligned to the bell, then pulled back inside both edges.
      const left = Math.min(Math.max(EDGE, r.right - width), Math.max(EDGE, vw - width - EDGE))
      const top = r.bottom + 6
      setBox({ top, left, width, maxHeight: Math.max(160, vh - top - EDGE) })
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [open])

  const handleOpen = async () => {
    const next = !open
    setOpen(next)
    if (next && notifications.length === 0) {
      setLoading(true)
      try {
        const data = await bidWorkupService.getNotifications()
        // Normalize: API may return { notifications: [...] } or a bare array
        const items = Array.isArray(data)
          ? data
          : Array.isArray(data?.notifications)
          ? data.notifications
          : []
        setNotifications(items)
      } catch { /* ignore */ } finally { setLoading(false) }
    }
  }

  const markAllRead = async () => {
    await bidWorkupService.markAllNotificationsRead().catch(() => {})
    setNotifications(n => n.map(x => ({ ...x, is_read: 1 })))
    setUnread(0)
  }

  const markOneRead = async (notif) => {
    if (notif.is_read) return
    await bidWorkupService.markNotificationRead(notif.id).catch(() => {})
    setNotifications(n => n.map(x => x.id === notif.id ? { ...x, is_read: 1 } : x))
    setUnread(c => Math.max(0, c - 1))
  }

  // ── CLICKING A NOTIFICATION NOW GOES THERE ────────────────────────────────────────────────────
  //
  // It used to mark the row read and stop. Every notification in this panel carries a `link` — the
  // store has had the column since it was created and six services write one — and not one of them
  // was ever followed, so "Invoice Generated" and "Bid Awarded!" told you something had happened and
  // left you to find it.
  //
  // The rule about WHICH route a link means lives in shared/notifications/notificationLink.js: it
  // repairs the two legacy shapes that never resolved, and refuses anything that is not a path this
  // app owns. See that file, and shared/app_links.py for the server half.
  const openNotification = (notif) => {
    markOneRead(notif)
    const route = routeForNotification(notif)
    if (!route) return          // nothing to open; the row is read and the panel stays put
    setOpen(false)
    navigate(route)
  }

  const totalBadge = unread + pendingReports.length

  const ICON = {
    status_change: <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />,
    comment: <MessageSquare className="h-4 w-4 text-blue-500 flex-shrink-0" />,
    // SOMEBODY IS WAITING ON YOU. Every other notification in this panel reports that something
    // happened; this one is a job to do, and it has to be findable among the rest — one staging
    // account is carrying 246 unread. Amber rather than the default grey bell for that reason.
    proposal_signature: <PenTool className="h-4 w-4 text-amber-600 flex-shrink-0" />,
  }

  const handleReviewClick = (report) => {
    const user = JSON.parse(localStorage.getItem('bfs_user') || '{}')
    const role = (user.role || '').toLowerCase()
    const canReview = APPROVAL_ROLES.includes(role) || role === 'timekeeper'
    const route = canReview ? `/timekeeping/review/${report.id}` : `/timekeeping/report/${report.id}`
    navigate(route)
    setOpen(false)
  }

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button onClick={handleOpen} className="ah-bell-btn" aria-label={t('notifications')}>
        {totalBadge > 0 ? <BellDot size={17} /> : <Bell size={17} />}
        {totalBadge > 0 && <span className="ah-bell-badge">{totalBadge > 99 ? '99+' : totalBadge}</span>}
      </button>

      {open && box && (
        <div style={{
          position: 'fixed', top: box.top, left: box.left, width: box.width,
          background: 'white', border: '1px solid #e5e7eb',
          borderRadius: 12, boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
          // The `popup` rung: above every modal, under the tour. Was a bare 9999.
          zIndex: Z.popup, overflow: 'hidden',
          display: 'flex', flexDirection: 'column', maxHeight: box.maxHeight,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{t('notifications')}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {unread > 0 && (
                <button onClick={markAllRead} style={{ fontSize: 12, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer' }}>{t('markAllRead')}</button>
              )}
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex' }}>
                <X size={16} color="#9ca3af" />
              </button>
            </div>
          </div>
          {/* The list takes whatever height is left after the title row, rather than a flat 400px
              that ran off the bottom of a 740px phone. */}
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {/* Pending Daily Report Approvals */}
            {pendingReports.length > 0 && (
              <>
                <div style={{
                  padding: '8px 16px', fontSize: 11, fontWeight: 700, color: '#92400e',
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                  background: '#fffbeb', borderBottom: '1px solid #fde68a',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <AlertTriangle size={12} style={{ color: '#d97706' }} />
                  {t('actionRequired', pendingReports.length)}
                </div>
                {(Array.isArray(pendingReports) ? pendingReports : []).map(r => (
                  <div
                    key={`pending-${r.id}`}
                    onClick={() => handleReviewClick(r)}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '10px 16px', cursor: 'pointer',
                      background: '#fefce8', borderBottom: '1px solid #fef9c3',
                      borderLeft: '3px solid #f59e0b',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fefce8'}
                  >
                    <Clock className="h-4 w-4 flex-shrink-0" style={{ color: '#d97706', marginTop: 2 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: '#111827', margin: 0 }}>
                        {r.foreman_name || t('dailyReport')} — {new Date(r.report_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                      <p style={{ fontSize: 12, color: '#92400e', margin: '2px 0 0' }}>
                        {t('statusLabel')}: {r.status === 'timekeeper_reviewed' ? t('reviewedNeedsApproval') : t('submittedNeedsReview')}
                      </p>
                    </div>
                    <span style={{ fontSize: 11, color: '#d97706', fontWeight: 600, whiteSpace: 'nowrap', marginTop: 2 }}>{t('review')} →</span>
                  </div>
                ))}
              </>
            )}
            {/* Bid workup / general notifications */}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                <Loader size={20} className="animate-spin" color="#9ca3af" />
              </div>
            )}
            {!loading && notifications.length === 0 && pendingReports.length === 0 && (
              <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: '32px 16px' }}>{t('noNotifications')}</p>
            )}
            {!loading && notifications.length === 0 && pendingReports.length > 0 && (
              <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12, padding: '16px' }}>{t('noOtherNotifications')}</p>
            )}
            {(Array.isArray(notifications) ? notifications : []).map(n => (
              <div
                key={n.id}
                onClick={() => openNotification(n)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '10px 16px', cursor: 'pointer',
                  background: !n.is_read ? '#eff6ff' : 'white',
                  borderBottom: '1px solid #f9fafb',
                }}
              >
                {ICON[n.category] || <Bell size={16} color="#9ca3af" style={{ flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: !n.is_read ? 600 : 400, color: !n.is_read ? '#111827' : '#374151', margin: 0 }}>{n.title}</p>
                  {n.body && <p style={{ fontSize: 12, color: '#6b7280', margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.body}</p>}
                  <p style={{ fontSize: 11, color: '#9ca3af', margin: '3px 0 0' }}>{parseUTC(n.created_at).toLocaleString()}</p>
                </div>
                {!n.is_read && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3b82f6', flexShrink: 0, marginTop: 4 }} />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default AppHeader
